import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

/** RL-1639: one "Notify doctor" notice from the dispensary. */
export interface OutOfStockNotice {
  id: string;
  type: 'out_of_stock';
  prescription: {
    id: string;
    number: string | null;
    encounterId: string;
    /** Review: a closed visit cannot take a replacement prescription. */
    visitClosed?: boolean;
    signedAt: string | null;
    dispensingStatus: string;
  };
  patient: { id: string; fullName: string };
  branch: { id: string; name: string };
  medicines: {
    itemId: string;
    medicine: string;
    quantity: number;
    unit: string | null;
  }[];
  note: string;
  prescriber: { id: string; fullName: string | null } | null;
  notifiedBy: { id: string; fullName: string | null } | null;
  notifiedAt: string;
  seenAt: string | null;
  seenBy: { id: string; fullName: string | null } | null;
}

export type NoticeStatus = 'open' | 'seen' | 'all';

/** GET /out-of-stock-notices (mine, or the branch for the manager). */
export function useOutOfStockNotices(status: NoticeStatus) {
  const { selected, canSelectAll } = useBranch();
  const branch = selected?.id ?? (canSelectAll ? 'all' : 'none');
  return useQuery({
    queryKey: ['out-of-stock-notices', status, branch],
    queryFn: ({ signal }) =>
      api
        .get<OutOfStockNotice[]>('/out-of-stock-notices', {
          signal,
          query: { status },
        })
        .then((r) => r.data),
    enabled: branch !== 'none',
    refetchInterval: 30_000,
  });
}

/** POST /out-of-stock-notices/{id}/seen (the prescriber). */
export function useMarkNoticeSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<OutOfStockNotice>(`/out-of-stock-notices/${id}/seen`, {}))
        .data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['out-of-stock-notices'] });
      void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
    },
  });
}
